import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'

import type { TeamMemberSummary } from '@aeris/contracts'
import { UNDENIABLE_PAGES } from '@aeris/permissions'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  InlineAlert,
  SearchInput,
  Spinner,
  cn,
} from '@aeris/ui'

import { fetchMemberPages, setMemberPages } from '../lib/api.ts'
import { GROUP_LABELS, GROUP_ORDER } from '../lib/module-groups.ts'
import { MODULES, type ModuleEntry } from '../lib/modules.tsx'

import { ROLE_LABELS } from './role-labels.ts'

/**
 * As telas que uma pessoa alcança (F038; arrumado na F136).
 *
 * ## O que esta tela NÃO é
 *
 * Não é um interruptor de menu. Desmarcar uma tela tira a permissão no
 * servidor, e a rota dela passa a responder 403 para essa pessoa — se fosse só
 * cosmético, a tela sumiria e o endereço continuaria funcionando para quem o
 * conhecesse.
 *
 * ## Por que o que o papel não alcança aparece travado, e não some
 *
 * Um admin que procura "Financeiro" na lista de um caixa e não encontra nada
 * conclui que a tela quebrou. Aparecer desmarcada, travada e explicada responde
 * a pergunta no lugar onde ela é feita: o caminho é mudar o papel.
 *
 * ## F136
 *
 * Eram quarenta caixas soltas, com a explicação escondida no `title` (que não
 * existe no toque). Agora: busca, "marcar todas" por seção, filhos recuados sob
 * o pai como no menu (Etiquetas sob Produtos), o motivo escrito na própria
 * linha e a conta de quantas estão marcadas.
 */
const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

/** Pai e, logo abaixo, os filhos dele — a ordem do menu lateral. */
const inMenuOrder = (
  entries: readonly ModuleEntry[]
): { entry: ModuleEntry; nested: boolean }[] => {
  const inGroup = new Set(entries.map((entry) => entry.module))
  const ordered: { entry: ModuleEntry; nested: boolean }[] = []
  for (const entry of entries) {
    if (entry.parent !== undefined && inGroup.has(entry.parent)) continue
    ordered.push({ entry, nested: false })
    for (const child of entries) {
      if (child.parent === entry.module) {
        ordered.push({ entry: child, nested: true })
      }
    }
  }
  return ordered
}

export const MemberPagesDialog = ({
  member,
  onClose,
}: {
  member: TeamMemberSummary
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const query = useQuery({
    queryKey: ['team', 'pages', member.userId],
    queryFn: ({ signal }) => fetchMemberPages(member.userId, signal),
  })

  /*
   * `null` enquanto o admin não mexeu em nada: o que vale então é a resposta do
   * servidor, derivada abaixo.
   *
   * Copiar a resposta para o estado num efeito seria a versão errada disso —
   * uma renderização a mais a cada abertura, e uma revalidação em segundo plano
   * apagando o que o admin acabou de marcar.
   */
  const [edited, setEdited] = useState<ReadonlySet<string> | null>(null)

  const available = useMemo(
    () => new Set(query.data?.available ?? []),
    [query.data]
  )

  /*
   * A ordem do menu, e não a ordem do catálogo: o admin está decidindo sobre
   * as mesmas telas que ele vê na barra lateral, e duas ordens diferentes para
   * a mesma lista fazem procurar item por item.
   *
   * Telas ocultas do menu entram: elas existem, a permissão delas é real, e
   * omiti-las aqui esconderia acesso de quem administra acesso.
   */
  const grouped = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({
        group,
        rows: inMenuOrder(MODULES.filter((entry) => entry.group === group)),
      })).filter((section) => section.rows.length > 0),
    []
  )

  const marked = useMemo(
    () => edited ?? new Set(query.data?.allowed ?? []),
    [edited, query.data]
  )

  const save = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async () => await setMemberPages(member.userId, [...marked]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['team'] })
      onClose()
    },
  })

  const editable = (module: string): boolean =>
    available.has(module) && !UNDENIABLE_PAGES.includes(module)

  const setMany = (modules: readonly string[], next: boolean): void => {
    const draft = new Set(marked)
    for (const module of modules) {
      if (!editable(module)) continue
      if (next) draft.add(module)
      else draft.delete(module)
    }
    setEdited(draft)
  }

  const term = normalize(search.trim())
  const visibleSections = grouped
    .map((section) => ({
      ...section,
      rows:
        term === ''
          ? section.rows
          : section.rows.filter(({ entry }) =>
              normalize(`${entry.label} ${entry.description}`).includes(term)
            ),
    }))
    .filter((section) => section.rows.length > 0)

  const reachableTotal = MODULES.filter((entry) =>
    available.has(entry.module)
  ).length
  const markedTotal = MODULES.filter(
    (entry) => available.has(entry.module) && marked.has(entry.module)
  ).length
  const roleLabel = ROLE_LABELS[member.role]

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Telas de ${member.displayName}`}
        description={`Vale só para esta pessoa. O papel ${roleLabel} continua definindo o máximo que ela pode alcançar.`}
        className="max-w-3xl"
      >
        {query.isPending ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SearchInput
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value)
                }}
                placeholder="Buscar tela"
                aria-label="Buscar tela"
                wrapperClassName="max-w-72"
              />
              <p
                aria-live="polite"
                className="text-(length:--text-sm) text-(--text-secondary)"
              >
                {`${String(markedTotal)} de ${String(reachableTotal)} telas do papel marcadas`}
              </p>
            </div>

            {visibleSections.length === 0 ? (
              <p className="text-(length:--text-sm) text-(--text-muted)">
                {`Nenhuma tela com “${search.trim()}”.`}
              </p>
            ) : null}

            {visibleSections.map((section) => {
              const modules = section.rows.map(({ entry }) => entry.module)
              const anyEditable = modules.some(editable)
              return (
                <section
                  key={section.group}
                  aria-label={GROUP_LABELS[section.group]}
                  className="flex flex-col gap-1"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-(--border-subtle) pb-1">
                    <h3 className="text-(length:--text-xs) font-medium uppercase tracking-wide text-(--text-muted)">
                      {GROUP_LABELS[section.group]}
                    </h3>
                    {anyEditable ? (
                      <span className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Marcar todas de ${GROUP_LABELS[section.group]}`}
                          onClick={() => {
                            setMany(modules, true)
                          }}
                        >
                          Marcar todas
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Desmarcar todas de ${GROUP_LABELS[section.group]}`}
                          onClick={() => {
                            setMany(modules, false)
                          }}
                        >
                          Nenhuma
                        </Button>
                      </span>
                    ) : null}
                  </div>

                  <ul className="grid gap-x-6 sm:grid-cols-2">
                    {section.rows.map(({ entry, nested }) => {
                      const reachable = available.has(entry.module)
                      const locked = UNDENIABLE_PAGES.includes(entry.module)
                      const reason = !reachable
                        ? `O papel ${roleLabel} não tem esta tela.`
                        : locked
                          ? 'Sempre disponível: é para onde o login leva.'
                          : null

                      return (
                        <li key={entry.module} className={cn(nested && 'pl-6')}>
                          <label
                            className={cn(
                              'flex items-start gap-2 rounded-(--radius-sm) px-2 py-1.5 text-(length:--text-sm)',
                              reachable
                                ? 'text-(--text-primary)'
                                : 'text-(--text-muted)'
                            )}
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={reachable && marked.has(entry.module)}
                              disabled={!reachable || locked}
                              onChange={(event) => {
                                setMany([entry.module], event.target.checked)
                              }}
                            />
                            <span className="flex flex-col">
                              <span>{entry.label}</span>
                              {reason === null ? null : (
                                <span className="text-(length:--text-xs) text-(--text-muted)">
                                  {reason}
                                </span>
                              )}
                            </span>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}

            {/*
              A frase que evita uma crença falsa.

              Desmarcar tira a tela do menu e da URL — não tranca o dado por
              baixo, quando outra tela marcada usa a mesma permissão: Vendas e
              Orçamentos compartilham `sales.read`, Produtos e Aplicações
              compartilham `catalog.read`. Um administrador que acredita ter
              revogado um acesso que continua alcançável está em situação pior
              do que um que sabe que não revogou, e a frase custa uma linha.
            */}
            <p className="text-(length:--text-xs) text-(--text-muted)">
              Desmarcar tira a tela do menu desta pessoa. Para tirar também o
              acesso aos dados por baixo, mude o papel dela.
            </p>

            {save.isError ? (
              <InlineAlert tone="danger">
                Não foi possível salvar. Tente de novo.
              </InlineAlert>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              save.mutate()
            }}
            disabled={query.isPending}
            loading={save.isPending}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
